import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import { placesApi } from '../../api/places.api';

interface Props {
  /** Google photo resource names (`places/…/photos/…`). */
  photos: string[];
  name: string;
  tripId?: string;
}

export default function SearchPhotoCarousel({ photos, name, tripId }: Props) {
  const [idx, setIdx] = useState(0);
  // Resource name → resolved temporary image URL. Each name is resolved at most
  // once (one billable Place Photo call); revisiting a photo reuses the URL.
  const [resolved, setResolved] = useState<Record<string, string | null>>({});
  const resolvingRef = useRef<Set<string>>(new Set());

  const current = photos[idx];
  const currentUrl = current ? resolved[current] : undefined;

  useEffect(() => {
    if (!current || current in resolved || resolvingRef.current.has(current)) return;
    resolvingRef.current.add(current);
    placesApi.photo(current, tripId)
      .then(url => setResolved(prev => ({ ...prev, [current]: url })))
      .catch(() => setResolved(prev => ({ ...prev, [current]: null })))
      .finally(() => resolvingRef.current.delete(current));
  }, [current, resolved, tripId]);

  const loading = current != null && !(current in resolved);
  // A photo Google refused (an expired name — the server forgets those details
  // so the next open brings fresh ones) leaves no picture: hide the frame
  // rather than show an empty grey box.
  if (current != null && resolved[current] === null) return null;

  return (
    <div className="relative h-44 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
      {/* Only the current photo is ever resolved/loaded, so each Place Photo is
          fetched from Google only when the user actually navigates to it. */}
      {loading && <div className="absolute inset-0 flex items-center justify-center"><Spinner size="md" /></div>}
      {!loading && currentUrl && (
        <img key={idx} src={currentUrl} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
      {photos.length > 1 && (
        <>
          <button
            onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center text-sm cursor-pointer transition-colors"
          >
            <Icon as={ChevronLeft} size={16} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % photos.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center text-sm cursor-pointer transition-colors"
          >
            <Icon as={ChevronRight} size={16} />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {photos.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        </>
      )}
      <div className="absolute top-2 right-2 bg-black/40 rounded-full px-2 py-0.5 text-[10px] text-white font-medium">
        {idx + 1}/{photos.length}
      </div>
    </div>
  );
}
